import React from 'react';
import {
  Check, X, ShieldCheck, Key, UserPlus, ToggleRight, ToggleLeft, UploadSimple, Trash
} from '@phosphor-icons/react';
import { DarkCard, SettingsToggle, ALL_PERMISSIONS } from './_shared';

const SettingsView = ({
  settings, subadmins, showAddSubadmin, subadminForm, changingPw, newPw,
  editingPerms, editPermsValue,
  setShowAddSubadmin, setSubadminForm, setChangingPw, setNewPw,
  setEditingPerms, setEditPermsValue,
  onToggleSetting, onCreateSubadmin, onToggleSubadmin, onChangePw, onDeleteSubadmin, onUpdatePermissions, onUploadLogo
}) => {
  const logoInputRef = React.useRef(null);
  const toggleFormPerm = (permId) => {
    setSubadminForm(f => ({
      ...f,
      permissions: f.permissions.includes(permId)
        ? f.permissions.filter(p => p !== permId)
        : [...f.permissions, permId]
    }));
  };
  const toggleEditPerm = (permId) => {
    setEditPermsValue(prev => prev.includes(permId) ? prev.filter(p => p !== permId) : [...prev, permId]);
  };

  return (
  <div>
    <p className="text-[#6b7a99] text-sm mb-6">Platform settings, feature management & subadmin controls</p>

    {/* Logo Management */}
    <DarkCard title="Platform Logo" data-testid="logo-management" className="mb-6">
      <div className="flex items-center gap-4">
        <div className="w-20 h-20 bg-[#0d1117] rounded-xl border border-[#252d3d] flex items-center justify-center overflow-hidden">
          {settings?.logo_url ? (
            <img src={settings.logo_url.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${settings.logo_url}` : settings.logo_url} alt="Logo" className="w-full h-full object-contain p-1" />
          ) : (
            <ShieldCheck size={32} className="text-[#6b7a99]" />
          )}
        </div>
        <div>
          <p className="text-white text-sm font-medium mb-1">Current Logo</p>
          <p className="text-[#6b7a99] text-xs mb-2">Upload a new logo (PNG, JPG, SVG, WebP. Max 5MB)</p>
          <input type="file" ref={logoInputRef} className="hidden" accept="image/png,image/jpeg,image/svg+xml,image/webp" onChange={e => { if (e.target.files?.[0]) onUploadLogo(e.target.files[0]); e.target.value = ''; }} />
          <button onClick={() => logoInputRef.current?.click()} className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg text-xs font-semibold transition-colors" data-testid="upload-logo-btn">
            <UploadSimple size={14} /> Change Logo
          </button>
        </div>
      </div>
    </DarkCard>

    {/* Feature Control Grid - 2x2 */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
      {/* Seller Feature Toggles */}
      <DarkCard title="Seller Features" data-testid="seller-feature-toggles">
        <p className="text-[#6b7a99] text-[11px] mb-3">Control what features are available to sellers</p>
        {settings ? (
          <>
            <SettingsToggle label="Global Country Selection" description="Allow sellers to add locations in any country"
              checked={settings.seller_country_selection !== false} onChange={() => onToggleSetting('seller_country_selection')} testId="toggle-seller-country" />
            <SettingsToggle label="Variant Media Upload" description="Allow photos/videos per space configuration"
              checked={settings.seller_variant_media !== false} onChange={() => onToggleSetting('seller_variant_media')} testId="toggle-seller-variant-media" />
            <SettingsToggle label="Coupons & Discounts" description="Allow sellers to create coupon codes"
              checked={settings.seller_coupons !== false} onChange={() => onToggleSetting('seller_coupons')} testId="toggle-seller-coupons" />
            <SettingsToggle label="Paid Ad Promotions" description="Allow sellers to buy ad placements"
              checked={settings.seller_paid_ads !== false} onChange={() => onToggleSetting('seller_paid_ads')} testId="toggle-seller-paid-ads" />
            <SettingsToggle label="Space Closures" description="Allow sellers to close spaces/slots for maintenance, holidays etc."
              checked={settings.seller_closures !== false} onChange={() => onToggleSetting('seller_closures')} testId="toggle-seller-closures" />
            <SettingsToggle label="Auto-Approve Listings" description="Skip manual approval for new listings"
              checked={settings.auto_approve_listings} onChange={() => onToggleSetting('auto_approve_listings')} testId="toggle-auto-approve" />
          </>
        ) : (
          <p className="text-[#6b7a99] text-sm">Loading settings...</p>
        )}
      </DarkCard>

      {/* Buyer Feature Toggles */}
      <DarkCard title="Buyer Features" data-testid="buyer-feature-toggles">
        <p className="text-[#6b7a99] text-[11px] mb-3">Control what features are available to buyers</p>
        {settings ? (
          <>
            <SettingsToggle label="Guest Browsing" description="Allow non-logged-in users to browse listings"
              checked={settings.guest_browsing} onChange={() => onToggleSetting('guest_browsing')} testId="toggle-guest-browsing" />
            <SettingsToggle label="Notifications" description="Enable email/push notifications"
              checked={settings.notifications_enabled} onChange={() => onToggleSetting('notifications_enabled')} testId="toggle-notifications" />
            <SettingsToggle label="Messaging" description="Enable buyer-seller chat"
              checked={settings.messaging_enabled} onChange={() => onToggleSetting('messaging_enabled')} testId="toggle-messaging" />
            <SettingsToggle label="Reviews" description="Allow buyers to leave reviews"
              checked={settings.reviews_enabled} onChange={() => onToggleSetting('reviews_enabled')} testId="toggle-reviews" />
            <SettingsToggle label="Wishlist" description="Allow buyers to save favorite listings"
              checked={settings.wishlist_enabled !== false} onChange={() => onToggleSetting('wishlist_enabled')} testId="toggle-wishlist" />
          </>
        ) : (
          <p className="text-[#6b7a99] text-sm">Loading settings...</p>
        )}
      </DarkCard>

      {/* Security Settings */}
      <DarkCard title="Security Settings" data-testid="security-settings">
        {settings ? (
          <>
            <SettingsToggle label="Email Verification Required" description="Require email verification on signup"
              checked={settings.email_verification_required} onChange={() => onToggleSetting('email_verification_required')} testId="toggle-email-verify" />
            <SettingsToggle label="Phone Verification Required" description="Require phone verification on signup"
              checked={settings.phone_verification_required} onChange={() => onToggleSetting('phone_verification_required')} testId="toggle-phone-verify" />
            <SettingsToggle label="Maintenance Mode" description="Take the site offline for maintenance"
              checked={settings.maintenance_mode} onChange={() => onToggleSetting('maintenance_mode')} testId="toggle-maintenance" />
          </>
        ) : (
          <p className="text-[#6b7a99] text-sm">Loading settings...</p>
        )}
      </DarkCard>
    </div>

    {/* Subadmin Management */}
    <div>
        <DarkCard title="" data-testid="subadmin-section">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-white font-semibold text-sm">Subadmin Management</h3>
              <p className="text-[#6b7a99] text-xs mt-0.5">{subadmins.length} subadmin{subadmins.length !== 1 ? 's' : ''}</p>
            </div>
            <button onClick={() => setShowAddSubadmin(!showAddSubadmin)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-xs font-semibold transition-colors"
              data-testid="add-subadmin-btn">
              <UserPlus size={14} weight="bold" /> Add Subadmin
            </button>
          </div>

          {/* Add Subadmin Form */}
          {showAddSubadmin && (
            <div className="bg-[#1c2333] rounded-xl p-4 mb-4 border border-[#252d3d]" data-testid="add-subadmin-form">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                <input type="text" value={subadminForm.name} onChange={(e) => setSubadminForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Full Name" className="h-9 px-3 bg-[#0d1117] border border-[#252d3d] rounded-lg text-white text-sm placeholder:text-[#6b7a99] focus:border-blue-500 outline-none"
                  data-testid="subadmin-name-input" />
                <input type="email" value={subadminForm.email} onChange={(e) => setSubadminForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="Email" className="h-9 px-3 bg-[#0d1117] border border-[#252d3d] rounded-lg text-white text-sm placeholder:text-[#6b7a99] focus:border-blue-500 outline-none"
                  data-testid="subadmin-email-input" />
                <input type="password" value={subadminForm.password} onChange={(e) => setSubadminForm(f => ({ ...f, password: e.target.value }))}
                  placeholder="Password" className="h-9 px-3 bg-[#0d1117] border border-[#252d3d] rounded-lg text-white text-sm placeholder:text-[#6b7a99] focus:border-blue-500 outline-none"
                  data-testid="subadmin-password-input" />
              </div>
              <p className="text-[#8896b0] text-xs font-medium mb-2">Permissions</p>
              <div className="flex flex-wrap gap-2 mb-3">
                {ALL_PERMISSIONS.map(perm => (
                  <button key={perm.id} onClick={() => toggleFormPerm(perm.id)}
                    className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors ${
                      subadminForm.permissions.includes(perm.id)
                        ? 'bg-indigo-500/20 text-indigo-300 border-indigo-500/40'
                        : 'bg-[#0d1117] text-[#6b7a99] border-[#252d3d] hover:text-white hover:border-[#3a4561]'
                    }`}
                    data-testid={`perm-form-${perm.id}`}>
                    {subadminForm.permissions.includes(perm.id) ? <Check size={12} weight="bold" className="inline mr-1" /> : null}
                    {perm.label}
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <button onClick={onCreateSubadmin} className="px-4 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-xs font-semibold" data-testid="save-subadmin-btn">Create</button>
                <button onClick={() => setShowAddSubadmin(false)} className="px-4 py-1.5 bg-[#252d3d] text-[#6b7a99] rounded-lg text-xs font-semibold hover:text-white">Cancel</button>
              </div>
            </div>
          )}

          {/* Subadmin List */}
          {subadmins.length === 0 ? (
            <div className="text-center py-8">
              <ShieldCheck size={40} className="text-[#252d3d] mx-auto mb-2" />
              <p className="text-[#6b7a99] text-sm">No subadmins yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {subadmins.map(sub => (
                <div key={sub.user_id} className={`rounded-xl border ${sub.active !== false ? 'bg-[#1c2333] border-[#252d3d]' : 'bg-[#0d1117] border-[#1c2333] opacity-60'}`}
                  data-testid={`subadmin-${sub.user_id}`}>
                  <div className="flex items-center gap-3 p-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${sub.active !== false ? 'bg-blue-500/20 text-blue-400' : 'bg-[#252d3d] text-[#6b7a99]'}`}>
                      {sub.name?.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-white text-sm font-medium truncate">{sub.name}</p>
                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${sub.active !== false ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                          {sub.active !== false ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                      </div>
                      <p className="text-[#6b7a99] text-xs truncate">{sub.email}</p>
                    </div>

                    {/* Change Password */}
                    {changingPw === sub.user_id ? (
                      <div className="flex items-center gap-1.5">
                        <input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)}
                          placeholder="New password" className="h-7 w-28 px-2 bg-[#0d1117] border border-[#252d3d] rounded text-white text-xs placeholder:text-[#6b7a99] focus:border-blue-500 outline-none"
                          data-testid={`pw-input-${sub.user_id}`} />
                        <button onClick={() => onChangePw(sub.user_id)} className="p-1 text-emerald-400 hover:text-emerald-300" data-testid={`pw-save-${sub.user_id}`}>
                          <Check size={16} weight="bold" />
                        </button>
                        <button onClick={() => { setChangingPw(null); setNewPw(''); }} className="p-1 text-[#6b7a99] hover:text-white">
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1">
                        <button onClick={() => { setEditingPerms(editingPerms === sub.user_id ? null : sub.user_id); setEditPermsValue(sub.permissions || []); }}
                          title="Edit Permissions" className="p-1.5 hover:bg-[#252d3d] rounded-lg" data-testid={`edit-perms-${sub.user_id}`}>
                          <ShieldCheck size={16} className="text-indigo-400" />
                        </button>
                        <button onClick={() => onToggleSubadmin(sub.user_id)} title={sub.active !== false ? 'Deactivate' : 'Activate'}
                          className="p-1.5 hover:bg-[#252d3d] rounded-lg" data-testid={`toggle-subadmin-${sub.user_id}`}>
                          {sub.active !== false ? <ToggleRight size={20} className="text-emerald-400" weight="fill" /> : <ToggleLeft size={20} className="text-[#6b7a99]" weight="fill" />}
                        </button>
                        <button onClick={() => { setChangingPw(sub.user_id); setNewPw(''); }} title="Change Password"
                          className="p-1.5 hover:bg-[#252d3d] rounded-lg" data-testid={`change-pw-${sub.user_id}`}>
                          <Key size={16} className="text-amber-400" />
                        </button>
                        <button onClick={() => onDeleteSubadmin(sub.user_id)} title="Delete"
                          className="p-1.5 hover:bg-red-500/20 rounded-lg" data-testid={`delete-subadmin-${sub.user_id}`}>
                          <Trash size={16} className="text-red-400" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Permissions row */}
                  <div className="px-3 pb-2.5">
                    {editingPerms === sub.user_id ? (
                      <div className="pt-2 border-t border-[#252d3d]">
                        <p className="text-[#8896b0] text-xs font-medium mb-2">Edit Permissions</p>
                        <div className="flex flex-wrap gap-1.5 mb-2">
                          {ALL_PERMISSIONS.map(perm => (
                            <button key={perm.id} onClick={() => toggleEditPerm(perm.id)}
                              className={`px-2 py-0.5 rounded text-[10px] font-medium border transition-colors ${
                                editPermsValue.includes(perm.id)
                                  ? 'bg-indigo-500/20 text-indigo-300 border-indigo-500/40'
                                  : 'bg-[#0d1117] text-[#6b7a99] border-[#252d3d] hover:text-white'
                              }`}
                              data-testid={`edit-perm-${perm.id}-${sub.user_id}`}>
                              {editPermsValue.includes(perm.id) ? <Check size={10} weight="bold" className="inline mr-0.5" /> : null}
                              {perm.label}
                            </button>
                          ))}
                        </div>
                        <div className="flex gap-1.5">
                          <button onClick={() => onUpdatePermissions(sub.user_id)}
                            className="px-3 py-1 bg-indigo-500 hover:bg-indigo-600 text-white rounded text-[10px] font-semibold"
                            data-testid={`save-perms-${sub.user_id}`}>Save</button>
                          <button onClick={() => setEditingPerms(null)}
                            className="px-3 py-1 bg-[#252d3d] text-[#6b7a99] rounded text-[10px] font-semibold hover:text-white">Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {(sub.permissions || []).length > 0 ? (sub.permissions || []).map(p => (
                          <span key={p} className="px-1.5 py-0.5 bg-indigo-500/10 text-indigo-300 rounded text-[9px] font-medium border border-indigo-500/20">
                            {ALL_PERMISSIONS.find(ap => ap.id === p)?.label || p}
                          </span>
                        )) : (
                          <span className="text-[#6b7a99] text-[10px] italic">No permissions assigned</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DarkCard>
      </div>
  </div>
  );
};

export default SettingsView;
